// ══════════════════════════════════════════════════════
// COMPONENTE COMPARTILHADO: NOTIFICATION TABLE
// ══════════════════════════════════════════════════════

import React from 'react';
import { Card, Table, Badge, Spinner, Button, Row, Col } from 'react-bootstrap';
import { FaEye, FaDownload, FaTrash, FaEnvelope, FaWhatsapp } from 'react-icons/fa';
import { NotificationStatus, NotificationType, NotificationChannel } from '../types/notification.types';

interface NotificationTableProps {
  loading: boolean;
  data: any[];
  channel: NotificationChannel;
  columns: string[];
  onView?: (id: string) => void;
  onDelete?: (id: string) => void;
  onExport?: () => void;
  emptyMessage?: string;
}

// Badges de Status
// const StatusBadge = ({ status }: { status: NotificationStatus }) => {
//   const variants: Record<NotificationStatus, string> = {
//     [NotificationStatus.SENT]: 'success',
//     [NotificationStatus.PENDING]: 'warning',
//     [NotificationStatus.FAILED]: 'danger',
//     [NotificationStatus.BOUNCED]: 'secondary',
//   };

//   const labels: Record<NotificationStatus, string> = {
//     [NotificationStatus.SENT]: '✓ Enviado',
//     [NotificationStatus.PENDING]: '⏳ Pendente',
//     [NotificationStatus.FAILED]: '✗ Falha',
//     [NotificationStatus.BOUNCED]: '↩ Devolvido',
//   };

//   return <Badge bg={variants[status]}>{labels[status]}</Badge>;
// };

    const StatusBadge = ({ status }: { status: NotificationStatus }) => {
    const colors: Record<NotificationStatus, string> = {
        [NotificationStatus.SENT]: '#198754',
        [NotificationStatus.PENDING]: '#ffc107',
        [NotificationStatus.FAILED]: '#dc3545',
        [NotificationStatus.BOUNCED]: '#6c757d',
    };

    const labels: Record<NotificationStatus, string> = {
        [NotificationStatus.SENT]: '✓ Enviado',
        [NotificationStatus.PENDING]: '⏳ Pendente',
        [NotificationStatus.FAILED]: '✗ Falha',
        [NotificationStatus.BOUNCED]: '↩ Devolvido',
    };

    return (
        <p
        style={{
            margin: 0,
            fontWeight: 500,
            color: colors[status],
        }}
        >
        {labels[status]}
        </p>
    );
};

// Badges de Tipo
// const TypeBadge = ({ type }: { type: NotificationType }) => {
//   const variants: Record<NotificationType, string> = {
//     [NotificationType.BEFORE_DUE]: 'info',
//     [NotificationType.DUE_TODAY]: 'warning',
//     [NotificationType.AFTER_DUE]: 'danger',
//     [NotificationType.PAYMENT_CONFIRM]: 'success',
//   };

//   const labels: Record<NotificationType, string> = {
//     [NotificationType.BEFORE_DUE]: 'Antes do Vencimento',   
//     [NotificationType.DUE_TODAY]: 'Vence Hoje',
//     [NotificationType.AFTER_DUE]: 'Após Vencimento',
//     [NotificationType.PAYMENT_CONFIRM]: 'Confirmação de Pagamento',
//   };

//   return <Badge bg={variants[type]}>{labels[type]}</Badge>;
// };

    const TypeBadge = ({ type }: { type: NotificationType }) => {
    const colors: Record<NotificationType, string> = {
        [NotificationType.BEFORE_DUE]: '#0dcaf0',
        [NotificationType.DUE_TODAY]: '#ffc107',
        [NotificationType.AFTER_DUE]: '#dc3545',
        [NotificationType.PAYMENT_CONFIRM]: '#198754',
    };

    const labels: Record<NotificationType, string> = {
        [NotificationType.BEFORE_DUE]: 'Antes do Vencimento',
        [NotificationType.DUE_TODAY]: 'Vence Hoje',
        [NotificationType.AFTER_DUE]: 'Após Vencimento',
        [NotificationType.PAYMENT_CONFIRM]: 'Confirmação de Pagamento',
    };

    return (
        <p
        style={{
            margin: 0,
            fontWeight: 500,
            color: colors[type],
        }}
        >
        {labels[type]}
        </p>
    );
    };

// Formatação de data
const formatDate = (date: Date | string | null) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function NotificationTable({
  loading,
  data,
  channel,
  columns,
  onView,
  onDelete,
  onExport,
  emptyMessage = 'Nenhuma notificação encontrada.',
}: NotificationTableProps) {
  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
        <h6 className="mb-0 d-flex align-items-center gap-2">
          {channel === 'EMAIL' ? (
            <>
              <FaEnvelope className="text-primary" />
              Histórico de E-mails
            </>
          ) : (
            <>
              <FaWhatsapp className="text-success" />
              Histórico de WhatsApp
            </>
          )}
        </h6>
        {onExport && (
          <Button variant="outline-secondary" size="sm" onClick={onExport}>
            <FaDownload className="me-2" />
            Exportar
          </Button>
        )}
      </Card.Header>

      <Card.Body className="p-0">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center p-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : data.length === 0 ? (
          <div className="text-center p-5 text-muted">
            <FaEnvelope size={40} className="mb-3 opacity-25" />
            <p>{emptyMessage}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  {columns.map((col) => (
                    <th key={col} className="border-bottom">{col}</th>
                  ))}
                  <th className="border-bottom text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={row.id || idx}>
                    <td>{row.customerName || row.customer?.name}</td>
                    <td>
                      {channel === 'EMAIL' ? (
                        <code style={{ fontSize: '0.85rem' }}>{row.email || row?.email}</code>
                      ) : (
                        <code style={{ fontSize: '0.85rem' }}>{row.phone || row?.phone}</code>
                      )}
                    </td>
                    <td>
                      <TypeBadge type={row.type} />
                    </td>
                    <td className="small">
                      {row.subject || (row.message?.substring(0, 40) + '...')}
                    </td>
                    <td>
                      <StatusBadge status={row.status?.toUpperCase() as NotificationStatus} />
                    </td>
                    <td className="small">{formatDate(row.sentAt)}</td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2">
                        {onView && (
                          <Button
                            variant="light"
                            size="sm"
                            onClick={() => onView(row.id)}
                            title="Visualizar"
                          >
                            <FaEye />
                          </Button>
                        )}
                        {onDelete && (
                          <Button
                            variant="light"
                            size="sm"
                            onClick={() => {
                              if (window.confirm('Deseja deletar este registro?')) {
                                onDelete(row.id);
                              }
                            }}
                            title="Deletar"
                          >
                            <FaTrash className="text-danger" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
